import {
  emergencySuccessionReserve,
  inventoryCost,
  monthlyEffectiveRate,
  presentValueAnnuityDue,
  protectionNeed,
} from '@fintracks/calculation-engine';
import { sumMoney } from './finance';
import { protectionMonthlyHouseholdExpense } from '@fintracks/contracts';
import type { ClientProtectionNeed, CreateProtectionNeedInput, GuaranteeExposure } from '@fintracks/contracts';

const GUARANTEE_KINDS = ['immobilized_property', 'monthly_income', 'financial_resources', 'life_insurance'] as const;

export function effectiveGuaranteeValue(exposures: readonly GuaranteeExposure[], excludedIds: ReadonlySet<string>): string | null {
  if (exposures.some((exposure) => exposure.bindingState !== 'verified' || !exposure.bindingReceipt || !exposure.bindingReceiptSha256 || !exposure.bindingExposureSha256)) return null;
  const resolvedKinds = new Set(exposures.map((exposure) => exposure.kind));
  if (exposures.length !== GUARANTEE_KINDS.length || resolvedKinds.size !== GUARANTEE_KINDS.length || GUARANTEE_KINDS.some((kind) => !resolvedKinds.has(kind))) return null;
  return sumMoney(exposures.filter((exposure) => !excludedIds.has(exposure.id)).map((exposure) => exposure.value));
}

export function calculateProtectionPlan(input: CreateProtectionNeedInput, current: ClientProtectionNeed | null = null): ClientProtectionNeed {
  const monthlyRate = monthlyEffectiveRate(input.realAnnualRatePct / 100);
  const totalExpenses = presentValueAnnuityDue(protectionMonthlyHouseholdExpense(input.monthlyLivingExpenses, input.dependents), monthlyRate, input.horizonYears * 12);
  const emergencyReserve = emergencySuccessionReserve(input.emergencyMonths, input.monthlyLivingExpenses).toNumber();
  const successionNeed = inventoryCost(input.estateBaseValue, input.legalFeesPct, input.courtCostsPct, input.inheritanceTaxPct).toNumber();
  const recommendedCapital = totalExpenses + emergencyReserve + successionNeed;
  const coverageGap = Math.max(0, protectionNeed(totalExpenses + emergencyReserve, successionNeed, input.currentGuarantees, {
    wholeLifeInsuredCapital: input.wholeLifeInsuredCapital,
    deductiblePrivatePension: input.deductiblePrivatePension,
  }).toNumber());
  return {
    id: current?.id ?? crypto.randomUUID(), clientId: input.clientId,
    monthlyLivingExpenses: input.monthlyLivingExpenses.toFixed(2), horizonYears: input.horizonYears,
    dependents: input.dependents, estateBaseValue: input.estateBaseValue.toFixed(2),
    legalFeesPct: input.legalFeesPct, courtCostsPct: input.courtCostsPct, inheritanceTaxPct: input.inheritanceTaxPct,
    emergencyMonths: input.emergencyMonths, currentGuarantees: input.currentGuarantees.toFixed(2), guaranteesMode: input.guaranteesMode, guaranteesScenarioId: input.guaranteesScenarioId,
    wholeLifeInsuredCapital: input.wholeLifeInsuredCapital.toFixed(2),
    deductiblePrivatePension: input.deductiblePrivatePension.toFixed(2), realAnnualRatePct: input.realAnnualRatePct,
    totalExpenses: totalExpenses.toFixed(2), emergencyReserve: emergencyReserve.toFixed(2),
    successionNeed: successionNeed.toFixed(2), recommendedCapital: recommendedCapital.toFixed(2),
    coverageGap: coverageGap.toFixed(2), version: (current?.version ?? 0) + 1,
    updatedAt: new Date().toISOString(),
  };
}

export function protectionCoveragePercent(value: ClientProtectionNeed): number {
  const recommended = Number(value.recommendedCapital);
  if (recommended <= 0) return 100;
  return Math.max(0, Math.min(100, Math.round((1 - Number(value.coverageGap) / recommended) * 100)));
}

export function protectionDependentsFromFamily(members: ReadonlyArray<{
  financiallyDependent: boolean;
  financialDependencyEnd: string | null;
}>): number {
  const today = new Date();
  return members.filter((member) => {
    if (!member.financiallyDependent) return false;
    if (!member.financialDependencyEnd) return true;
    const end = Date.parse(`${member.financialDependencyEnd}T23:59:59.999Z`);
    return Number.isFinite(end) && end >= today.getTime();
  }).length;
}
